// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { ExpressRoutePortsLocations } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type {
  ExpressRoutePortsLocation,
  ExpressRoutePortsLocationsListNextOptionalParams,
  ExpressRoutePortsLocationsListOptionalParams,
  ExpressRoutePortsLocationsListResponse,
  ExpressRoutePortsLocationsGetOptionalParams,
  ExpressRoutePortsLocationsGetResponse,
  ExpressRoutePortsLocationsListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing ExpressRoutePortsLocations operations. */
export class ExpressRoutePortsLocationsImpl implements ExpressRoutePortsLocations {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class ExpressRoutePortsLocations class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Retrieves all ExpressRoutePort peering locations. Does not return available bandwidths for each
   * location. Available bandwidths can only be obtained when retrieving a specific peering location.
   * @param options The options parameters.
   */
  public list(
    options?: ExpressRoutePortsLocationsListOptionalParams,
  ): PagedAsyncIterableIterator<ExpressRoutePortsLocation> {
    const iter = this.listPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(options, settings);
      },
    };
  }

  private async *listPagingPage(
    options?: ExpressRoutePortsLocationsListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<ExpressRoutePortsLocation[]> {
    let result: ExpressRoutePortsLocationsListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    options?: ExpressRoutePortsLocationsListOptionalParams,
  ): AsyncIterableIterator<ExpressRoutePortsLocation> {
    for await (const page of this.listPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Retrieves all ExpressRoutePort peering locations. Does not return available bandwidths for each
   * location. Available bandwidths can only be obtained when retrieving a specific peering location.
   * @param options The options parameters.
   */
  private _list(
    options?: ExpressRoutePortsLocationsListOptionalParams,
  ): Promise<ExpressRoutePortsLocationsListResponse> {
    return this.client.sendOperationRequest({ options }, listOperationSpec);
  }

  /**
   * Retrieves a single ExpressRoutePort peering location, including the list of available bandwidths
   * available at said peering location.
   * @param locationName Name of the requested ExpressRoutePort peering location.
   * @param options The options parameters.
   */
  get(
    locationName: string,
    options?: ExpressRoutePortsLocationsGetOptionalParams,
  ): Promise<ExpressRoutePortsLocationsGetResponse> {
    return this.client.sendOperationRequest({ locationName, options }, getOperationSpec);
  }

  /**
   * ListNext
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    nextLink: string,
    options?: ExpressRoutePortsLocationsListNextOptionalParams,
  ): Promise<ExpressRoutePortsLocationsListNextResponse> {
    return this.client.sendOperationRequest({ nextLink, options }, listNextOperationSpec);
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Network/ExpressRoutePortsLocations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ExpressRoutePortsLocationListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Network/ExpressRoutePortsLocations/{locationName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ExpressRoutePortsLocation,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.locationName],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ExpressRoutePortsLocationListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer,
};
